import React, { useEffect, useMemo, useRef, useState } from 'react';
import { localizedValue } from '../localization/presentationLanguage';
const feedText = (es, en) => localizedValue({ es, en });

function normalizeText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export default function FeedCommentComposer({
  onSubmit,
  placeholder,
  submitLabel,
  disabled = false,
  submitting = false,
  minLength = 1,
  maxLength = 300,
  className = '',
}) {
  const resolvedPlaceholder = placeholder || feedText('Escribí una respuesta cósmica...', 'Write a cosmic reply...');
  const resolvedSubmitLabel = submitLabel || feedText('Comentar', 'Comment');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [successPulse, setSuccessPulse] = useState(false);
  const textareaRef = useRef(null);

  const normalizedText = useMemo(() => normalizeText(text), [text]);
  const rawLength = useMemo(() => String(text || '').length, [text]);
  const remaining = useMemo(() => Math.max(0, maxLength - rawLength), [maxLength, rawLength]);
  const isTooShort = useMemo(
    () => normalizedText.length > 0 && normalizedText.length < minLength,
    [normalizedText, minLength]
  );
  const isTooLong = useMemo(() => rawLength > maxLength, [rawLength, maxLength]);
  const isNearLimit = useMemo(() => remaining <= 50 && !isTooLong, [remaining, isTooLong]);

  const canSubmit = useMemo(() => {
    return (
      typeof onSubmit === 'function' &&
      !disabled &&
      !submitting &&
      normalizedText.length >= minLength &&
      rawLength <= maxLength
    );
  }, [onSubmit, disabled, submitting, normalizedText, minLength, rawLength, maxLength]);

  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;

    node.style.height = '0px';
    const nextHeight = Math.min(Math.max(node.scrollHeight, 84), 220);
    node.style.height = `${nextHeight}px`;
  }, [text]);

  useEffect(() => {
    if (!successPulse) return undefined;

    const timeout = window.setTimeout(() => {
      setSuccessPulse(false);
    }, 900);

    return () => window.clearTimeout(timeout);
  }, [successPulse]);

  const handleChange = (event) => {
    setText(event?.target?.value || '');
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const safeText = normalizeText(text);

    if (!safeText) {
      setError(feedText('Escribí un comentario para continuar.', 'Write a comment to continue.'));
      return;
    }

    if (safeText.length < minLength) {
      setError(feedText(`Tu comentario debe tener al menos ${minLength} caracter${minLength === 1 ? '' : 'es'}.`, `Your comment must be at least ${minLength} character${minLength === 1 ? '' : 's'} long.`));
      return;
    }

    if (String(text || '').length > maxLength) {
      setError(feedText(`Tu comentario no puede superar los ${maxLength} caracteres.`, `Your comment cannot exceed ${maxLength} characters.`));
      return;
    }

    if (typeof onSubmit !== 'function' || disabled || submitting) {
      return;
    }

    try {
      await onSubmit({ text: safeText });
      setText('');
      setError('');
      setSuccessPulse(true);
    } catch (err) {
      setError(err?.message || feedText('No se pudo publicar el comentario.', 'The comment could not be posted.'));
    }
  };

  const handleKeyDown = async (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      await handleSubmit(event);
    }
  };

  return (
    <form className={`feed-comment-composer ${className}`.trim()} onSubmit={handleSubmit}>
      <div
        className="feed-comment-composer__inner"
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 18,
          border: successPulse
            ? '1px solid rgba(255,215,0,0.18)'
            : '1px solid rgba(255,255,255,0.06)',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.028))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: 12,
          boxShadow: successPulse
            ? '0 16px 30px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.02) inset, 0 0 26px rgba(255,215,0,0.10)'
            : '0 16px 30px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.02) inset',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(180px 90px at 0% 0%, rgba(255,215,0,0.08), rgba(255,215,0,0) 60%), radial-gradient(160px 90px at 100% 0%, rgba(132,79,255,0.10), rgba(132,79,255,0) 60%)',
            pointerEvents: 'none',
          }}
        />

        <textarea
          id="feedCommentText"
          name="feedCommentText"
          ref={textareaRef}
          className={`feed-comment-composer__textarea ${error ? 'has-error' : ''}`.trim()}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          rows={3}
          maxLength={maxLength + 50}
          disabled={disabled || submitting}
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            minHeight: 84,
            height: 84,
            resize: 'none',
            overflowY: rawLength > 180 ? 'auto' : 'hidden',
            borderRadius: 14,
            border: error
              ? '1px solid rgba(255,88,88,0.24)'
              : '1px solid rgba(255,255,255,0.08)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.04))',
            color: '#FFF',
            padding: '13px 13px 11px',
            fontSize: 16,
            lineHeight: 1.45,
            transform: 'translateZ(0)',
            WebkitTextSizeAdjust: '100%',
            outline: 'none',
            transition: 'border-color 160ms ease, box-shadow 160ms ease, background 160ms ease',
            caretColor: '#FFD700',
            boxShadow: error
              ? '0 0 0 1px rgba(255,88,88,0.08) inset'
              : '0 8px 18px rgba(255,255,255,0.03) inset',
          }}
        />

        <div
          className="feed-comment-composer__footer"
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 10,
          }}
        >
          <div className="feed-comment-composer__meta" style={{ minWidth: 0 }}>
            {error ? (
              <p
                className="feed-comment-composer__message feed-comment-composer__message--error"
                style={{
                  margin: 0,
                  color: '#FFDADA',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {error}
              </p>
            ) : isTooShort && rawLength > 0 ? (
              <p
                className="feed-comment-composer__message"
                style={{
                  margin: 0,
                  color: 'rgba(245,240,255,0.68)',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {feedText(`Tu comentario debe tener al menos ${minLength} caracter${minLength === 1 ? '' : 'es'}.`, `Your comment must be at least ${minLength} character${minLength === 1 ? '' : 's'} long.`)}
              </p>
            ) : (
              <p
                className="feed-comment-composer__message"
                style={{
                  margin: 0,
                  color: successPulse ? '#FFE7A6' : 'rgba(245,240,255,0.54)',
                  fontSize: 11.5,
                  fontWeight: 700,
                  transition: 'color 180ms ease',
                }}
              >
                {successPulse ? feedText('Comentario enviado con éxito.', 'Comment posted successfully.') : ''}
              </p>
            )}
          </div>

          <div
            className="feed-comment-composer__actions"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginLeft: 'auto',
              flexWrap: 'wrap',
            }}
          >
            <span
              className={`feed-comment-composer__counter ${isNearLimit ? 'is-warning' : ''} ${isTooLong ? 'is-danger' : ''}`.trim()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
                padding: '6px 9px',
                borderRadius: 999,
                background: successPulse
                  ? 'rgba(255,215,0,0.14)'
                  : isTooLong
                    ? 'rgba(255,88,88,0.12)'
                    : isNearLimit
                      ? 'rgba(255,215,0,0.12)'
                      : 'rgba(255,255,255,0.04)',
                border: successPulse
                  ? '1px solid rgba(255,215,0,0.22)'
                  : isTooLong
                    ? '1px solid rgba(255,88,88,0.18)'
                    : isNearLimit
                      ? '1px solid rgba(255,215,0,0.18)'
                      : '1px solid rgba(255,255,255,0.06)',
                color: successPulse
                  ? '#FFE082'
                  : isTooLong
                    ? '#FFD9D9'
                    : isNearLimit
                      ? '#FFE082'
                      : 'rgba(255,245,220,0.70)',
                fontSize: 11,
                fontWeight: 1000,
                letterSpacing: 0.24,
              }}
            >
              {remaining}
            </span>

            <button
              type="submit"
              className="feed-comment-composer__submit"
              disabled={!canSubmit}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                border: '1px solid rgba(255,215,0,0.58)',
                borderRadius: 999,
                padding: '9px 15px',
                minWidth: 126,
                background:
                  'linear-gradient(135deg, #FFD700 0%, #FFE082 40%, #FFC107 60%, #FFB300 100%)',
                color: '#2A1800',
                fontWeight: 1000,
                fontSize: 12.5,
                letterSpacing: 0.28,
                cursor: 'pointer',
                boxShadow:
                  '0 10px 22px rgba(0,0,0,0.22), 0 0 16px rgba(255,215,0,0.26), 0 0 6px rgba(255,255,255,0.22) inset',
                opacity: !canSubmit ? 0.78 : 1,
                transform: successPulse ? 'translateY(-1px)' : 'translateY(0px)',
                filter: successPulse ? 'brightness(1.03)' : 'none',
                transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease, opacity 160ms ease',
              }}
            >
              {submitting ? feedText('Comentando...', 'Commenting...') : successPulse ? feedText('Enviado', 'Sent') : resolvedSubmitLabel}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .feed-comment-composer__submit:hover:not(:disabled),
        .feed-comment-composer__submit:focus-visible:not(:disabled) {
          transform: translateY(-1px);
          box-shadow:
            0 12px 24px rgba(0,0,0,0.24),
            0 0 18px rgba(255,215,0,0.30),
            0 0 6px rgba(255,255,255,0.22) inset;
          filter: brightness(1.02);
        }

        .feed-comment-composer__textarea::placeholder {
          color: rgba(255, 245, 220, 0.46);
        }

        .feed-comment-composer__textarea:focus {
          border-color: rgba(255, 215, 0, 0.22) !important;
          box-shadow:
            0 0 0 1px rgba(255,215,0,0.08) inset,
            0 0 0 3px rgba(255,215,0,0.05),
            0 10px 22px rgba(255,255,255,0.04) inset !important;
          background: linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.05));
        }
      `}</style>
    </form>
  );
}
